import React from "react";
import styles from "../../../Admin/widget/WidgetAnalytics.module.css";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell,
} from "recharts";

const fmt = (n) => n.toLocaleString();

const LEGEND_ITEMS = [
  { label: "Visitors",     color: "#6366f1", key: "visitors"    },
  { label: "Page Views",   color: "#f59e0b", key: "pageViews"   },
  { label: "Widget Opens", color: "#10b981", key: "widgetOpens" },
  { label: "WA Clicks",    color: "#0ea5e9", key: "waClicks"    },
];

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: "#fff", border: "1.5px solid #e8edf5", borderRadius: "12px",
      padding: "10px 14px", boxShadow: "0 8px 24px rgba(15,23,42,0.12)",
      fontFamily: "'Inter', sans-serif", minWidth: "160px",
    }}>
      <div style={{ fontSize: "10px", fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: "8px" }}>
        {label}
      </div>
      {payload.map((p, i) => (
        <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px", marginBottom: "4px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <span style={{ width: 7, height: 7, borderRadius: "50%", background: p.color, display: "inline-block" }} />
            <span style={{ fontSize: "11px", color: "#64748b", fontWeight: 500 }}>{p.name}</span>
          </div>
          <span style={{ fontSize: "12px", fontWeight: 700, color: "#0f172a" }}>{fmt(p.value)}</span>
        </div>
      ))}
    </div>
  );
};

const SkeletonChartSection = () => (
  <div className="row mb-4">
    <div className="col-lg-8 col-md-7 mb-3">
      <div className={styles.skeletonChartCard}>
        <div className={styles.skeletonChartHeader}>
          <div className={styles.skeletonChartFlex}>
            <div className={styles.skeleton} style={{ height: "15px", width: "45%", marginBottom: "8px" }} />
            <div className={styles.skeleton} style={{ height: "10px", width: "70%" }} />
          </div>
          <div className={styles.skeletonLegendRow}>
            {[60, 65, 72, 58].map((w, i) => (
              <div key={i} className={styles.skeleton} style={{ height: "22px", width: w, borderRadius: "6px" }} />
            ))}
          </div>
        </div>
        <div className={`${styles.skeleton} ${styles.skeletonChartArea}`} style={{ height: "280px" }} />
      </div>
    </div>
    <div className="col-lg-4 col-md-5 mb-3">
      <div className={`${styles.skeletonRightCard} mb-3`}>
        <div className={styles.skeleton} style={{ height: "10px", width: "50%", marginBottom: "6px" }} />
        <div className={styles.skeleton} style={{ height: "9px", width: "80%", marginBottom: "4px" }} />
        <div className={styles.skeletonFunnelRow}>
          {[100, 80, 44, 20, 13].map((pct, i) => (
            <div key={i} className={styles.skeletonFunnelItem}>
              <div className={styles.skeletonFunnelItemRow}>
                <div className={styles.skeleton} style={{ height: "10px", width: "35%" }} />
                <div className={styles.skeleton} style={{ height: "10px", width: "25%" }} />
              </div>
              <div className={styles.skeleton} style={{ height: "6px", width: `${pct}%`, borderRadius: "4px" }} />
            </div>
          ))}
        </div>
      </div>
      <div className={styles.skeletonRightCard}>
        <div className={styles.skeleton} style={{ height: "10px", width: "55%", marginBottom: "6px" }} />
        <div className={styles.skeleton} style={{ height: "9px", width: "75%", marginBottom: "14px" }} />
        <div className={styles.skeletonDeviceRow}>
          <div className={styles.skeleton} style={{ width: "110px", height: "110px", borderRadius: "50%", flexShrink: 0 }} />
          <div className={styles.skeletonDeviceLegList}>
            {[0, 1, 2].map((i) => (
              <div key={i} className={styles.skeletonDeviceLegItem}>
                <div className={styles.skeleton} style={{ width: "9px", height: "9px", borderRadius: "50%", flexShrink: 0 }} />
                <div className={styles.skeleton} style={{ height: "10px", flex: 1 }} />
                <div className={styles.skeleton} style={{ height: "10px", width: "30px" }} />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  </div>
);

const fmtSessions = (n) => (n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(n));

const AnalyticsSection = ({ chartData, funnelData, deviceData, totalSessions, loading }) => {
  if (loading) return <SkeletonChartSection />;

  return (
    <div className="row mb-4">
      <div className="col-lg-8 col-md-7 mb-3">
        <div className={styles.chartCard}>
          <div className={styles.chartHeader}>
            <div>
              <div className={styles.chartTitle}>Interaction Analytics</div>
              <div className={styles.chartSubtitle}>Visitors, Widget Opens and WhatsApp Clicks over time</div>
            </div>
            <div className={styles.chartLegend}>
              {LEGEND_ITEMS.map((l, i) => (
                <span key={i} className={styles.legendItem}>
                  <span className={styles.legendDot} style={{ background: l.color }} />
                  {l.label}
                </span>
              ))}
            </div>
          </div>
          <div className={styles.chartHoverHint}>Hover graph points to inspect telemetry</div>
          <ResponsiveContainer width="100%" height={268}>
            <AreaChart data={chartData} margin={{ top: 6, right: 8, left: -10, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
              <XAxis dataKey="date" tick={{ fontSize: 10.5, fill: "#94a3b8", fontFamily: "Inter" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 10.5, fill: "#94a3b8", fontFamily: "Inter" }} axisLine={false} tickLine={false} tickFormatter={(v) => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v} />
              <Tooltip content={<CustomTooltip />} />
              <Area type="monotone" dataKey="visitors"    stroke="#6366f1" strokeWidth={2.5} fill="none" dot={false} name="Visitors"      activeDot={{ r: 5, fill: "#6366f1", stroke: "#fff", strokeWidth: 2 }} />
              <Area type="monotone" dataKey="pageViews"   stroke="#f59e0b" strokeWidth={2}   fill="none" dot={false} name="Page Views"    activeDot={{ r: 4, fill: "#f59e0b", stroke: "#fff", strokeWidth: 2 }} />
              <Area type="monotone" dataKey="widgetOpens" stroke="#10b981" strokeWidth={2}   fill="none" dot={false} name="Widget Opens"  activeDot={{ r: 4, fill: "#10b981", stroke: "#fff", strokeWidth: 2 }} />
              <Area type="monotone" dataKey="waClicks"    stroke="#0ea5e9" strokeWidth={2}   fill="none" dot={false} name="WA Clicks"     activeDot={{ r: 4, fill: "#0ea5e9", stroke: "#fff", strokeWidth: 2 }} />
            </AreaChart>
          </ResponsiveContainer>
          <div className={styles.chartFooter}>
            <span className={styles.chartScaleNote}>Continuous linear representation of user intent telemetry</span>
            <span className={styles.chartIntervalNote}>Daily aggregates</span>
          </div>
        </div>
      </div>

      <div className="col-lg-4 col-md-5 mb-3">
        <div className={`${styles.rightCard} mb-3`}>
          <div className={styles.rightCardLabel}>Conversion Funnel</div>
          <div className={styles.rightCardSubLabel}>Pipeline conversion from visitors to chats</div>
          <div className={styles.funnelList}>
            {funnelData.map((f, i) => (
              <div key={i}>
                <div className={styles.funnelTop}>
                  <span className={styles.funnelLabel}>{f.label}</span>
                  <span className={styles.funnelValue}>{fmt(f.value)} <span className={styles.funnelPct}>({f.pct}%)</span></span>
                </div>
                <div className={styles.funnelBarTrack}>
                  <div className={styles.funnelBarFill} style={{ width: `${f.pct}%`, background: f.color }} />
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className={styles.rightCard}>
          <div className={styles.rightCardLabel}>Device Distribution</div>
          <div className={styles.rightCardSubLabel}>Segmented interaction device ratios</div>
          <div className={styles.deviceInner}>
            <div className={styles.deviceDonutWrap}>
              <PieChart width={110} height={110}>
                <Pie data={deviceData} cx={50} cy={50} innerRadius={33} outerRadius={50} dataKey="value" startAngle={90} endAngle={-270} strokeWidth={0}>
                  {deviceData.map((d, i) => <Cell key={i} fill={d.color} />)}
                </Pie>
              </PieChart>
              <div className={styles.deviceDonutCenter}>
                <div className={styles.deviceDonutVal}>{fmtSessions(totalSessions || 0)}</div>
                <div className={styles.deviceDonutLbl}>Sessions</div>
              </div>
            </div>
            <div className={styles.deviceLegend}>
              {deviceData.map((d, i) => (
                <div key={i} className={styles.deviceLegendItem}>
                  <span className={styles.deviceDot} style={{ background: d.color }} />
                  <span className={styles.deviceLegendLabel}>{d.name}</span>
                  <span className={styles.deviceLegendPct}>{d.value}%</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsSection;
